import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  CollectingRequestValidation,
  GetCollectingRequestCandidateValidationParams,
  GetCollectingRequestCandidateValidationResponse,
} from '~/types/delivery-agency.types';

const fetchCollectingRequestCandidateValidation = async (
  params: GetCollectingRequestCandidateValidationParams
): Promise<CollectingRequestValidation> => {
  const { data } = await axiosInstance.get<GetCollectingRequestCandidateValidationResponse>(
    `${API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_REQUEST_CANDIDATE_DETAIL}/${params.outboundCode}/validation`
  );
  return data.data;
};

/**
 * [반품 신청 1step] 반품 정보 입력 단계로 넘어가기 전 사전검증
 *
 * 과거 회수예정일에 반품 신청 이력이 있는 출고번호면 경고를 내려준다.
 * 경고는 차단이 아니라 확인용이라 사용자가 확인하면 그대로 진행한다.
 * 중복 접수 등 에러 문구는 사용처에서 얼럿으로 노출한다.
 */
export const useGetCollectingRequestCandidateValidationMutation = () => {
  return useMutation<
    CollectingRequestValidation,
    TypeErrorResponseData['error'],
    GetCollectingRequestCandidateValidationParams
  >({
    mutationFn: fetchCollectingRequestCandidateValidation,
    meta: { showGlobalErrorToast: false },
  });
};

registerMockAPI<GetCollectingRequestCandidateValidationResponse | TypeErrorResponseData>({
  method: 'get',
  endpoint: `${API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_REQUEST_CANDIDATE_DETAIL}/:outboundCode([A-Z]{0,}\\d[A-Z0-9]{0,})/validation`,
  label: '배송 주문 반품 신청·조회/반품 신청 사전검증',
  scenarios: [
    {
      name: '경고 없음',
      response: { data: { warnings: [] } },
    },
    {
      name: '이전 반품 신청 이력 있음',
      response: {
        data: {
          warnings: [
            {
              code: 'COLLECTING_REQUEST_EXISTS_BEFORE',
              message: '이전에 반품 접수한 이력이 있는 주문입니다.\n계속 신청하시겠습니까?',
            },
          ],
        },
      },
    },
    {
      // 문구는 백엔드가 내려주는 값을 그대로 노출한다. 에러 코드는 미확인이라 비워 둔다
      name: '같은 반품접수일 중복 신청',
      status: 400,
      response: {
        error: {
          message: '이미 반품 접수가 완료된 주문입니다.\n같은 반품접수일에 반품을 중복으로 신청할 수 없습니다.',
        },
      },
    },
  ],
});
